import { decodeJwtDuties } from "../features/auth/api/sessionToken";

// SCOPING, NOT SECURITY. Proved live 2026-09-03: every BOLON account is
// `role: "member"` (the `bolon_admin` DUTY is a different axis from the
// gateway's `role`), and a member token reaches the
// routes api-gateway.yaml marks `[admin]`-only — /user-groups, /ecps and
// /users all answered 200. The gateway checks AUTHENTICATION, not
// authorization, and no endpoint anywhere reads `duties`.
//
// So `bolon_sales_manager` is a flag the backend issues and only this UI acts
// on. These gates keep people out of screens that are not their job; they do
// not protect the data behind them, because a devtools fetch — or a changed
// VITE_DEV_DUTIES — walks straight past. Do not describe them as access
// control, and do not rely on them for anything confidential.
// See BACKEND-REQUEST.md item 18.
//
// Internal duty names the app's own RequireDuty/useHasDuty gates check —
// distinct from the backend's duty vocabulary (bolon_sales_manager,
// bolon_sales, qc, support, task, keyin, reward, crm, ...). There is still no
// finer-grained backend duty per feature, so the SOURCE remains binary: one
// claim decides which of the two sets below a user gets.
//
// The sets themselves are no longer "base shell" vs "everything" (2026-09-03).
// Staff do the day-to-day work — orders, claims, tiers — so those moved into
// the staff set. What stays above staff is the oversight and configuration:
//
//   dashboard    aggregate SALE VALUE across every sales group — ADMIN only
//                since 2026-09-04; a sales manager does not see it either
//   catalog      products, brands, categories, materials, stock
//   user-groups  which ECP belongs to which group (and so Sales Group)
//
// "bolon" is still the always-present duty that gates the shell itself, so it
// no longer implies the dashboard — that is now its own "dashboard" duty.
// Without splitting them, gating the dashboard would lock staff out of the
// whole app.
const ADMIN_DUTY = "bolon_admin";
const MANAGER_DUTY = "bolon_sales_manager";

/*
 * Staff = the day-to-day desk: orders and claims. Tiers and Customer
 * Achievement moved UP to the manager tier on 2026-09-04 (user call: "bolon
 * sales cannot see them") — both are programme configuration, not order
 * handling.
 */
const STAFF_DUTIES: readonly string[] = Object.freeze([
  "bolon",
  "orders",
  "claims",
]);

/*
 * THREE tiers since 2026-09-04 (user decision), still decided by one claim:
 *
 *   bolon_admin          everything, including the dashboard
 *   bolon_sales_manager  catalog + user groups on top of staff — but NOT the
 *                        dashboard: aggregate sale value across every sales
 *                        group is the admin's figure, not the sales manager's
 *   anything else        staff: orders, claims, tiers
 *
 * `bolon_admin` is a duty like the others, issued by the backend on the
 * ClaimsToken; the gateway still enforces nothing (see the header note).
 */
const MANAGER_DUTIES: readonly string[] = Object.freeze([
  ...STAFF_DUTIES,
  "tiers",
  "achievements",
  "catalog",
  "user-groups",
  // Complete Order. Staff only Submit (user call 2026-10-20, ESS-177);
  // completing is the manager's/admin's step.
  "complete-orders",
]);

const ADMIN_DUTIES: readonly string[] = Object.freeze([
  ...MANAGER_DUTIES,
  "dashboard",
  // Editing the DEFAULT tier. A manager can open its pages but sees them
  // read-only: a type, period, level or milestone change there is applied to
  // every group retroactively (progress moves cycle_year, rewards recompute).
  "default-tier-edit",
]);

export type BolonRole = "admin" | "manager" | "staff";

/*
 * Host-app addition (migration D10). Inside fe-essilor-dashboard every kind of
 * member signs in through the same door, so a KEYIN-only account can type
 * /bolon/orders. The standalone app never met that account and treated ANY
 * token with a duties claim as "staff". Here: no bolon_* duty means not a
 * Bolon user at all — RequireDuty("bolon") then renders Access denied.
 */
const BOLON_DUTY_PREFIX = "bolon_";

export function resolveRoleFromDuties(
  backendDuties: readonly string[] | undefined,
): BolonRole | undefined {
  if (backendDuties === undefined) return undefined;
  if (!backendDuties.some((duty) => duty.startsWith(BOLON_DUTY_PREFIX))) {
    return undefined;
  }
  if (backendDuties.includes(ADMIN_DUTY)) return "admin";
  if (backendDuties.includes(MANAGER_DUTY)) return "manager";
  return "staff";
}

export function resolveDutiesFromBackendDuties(
  backendDuties: readonly string[] | undefined,
): readonly string[] {
  const role = resolveRoleFromDuties(backendDuties);
  if (role === undefined) return [];
  return role === "admin"
    ? ADMIN_DUTIES
    : role === "manager"
      ? MANAGER_DUTIES
      : STAFF_DUTIES;
}

export function resolveRoleFromToken(token: string): BolonRole | undefined {
  return resolveRoleFromDuties(decodeJwtDuties(token));
}

export function resolveDutiesFromToken(
  token: string,
): readonly string[] | undefined {
  const duties = decodeJwtDuties(token);
  return resolveRoleFromDuties(duties) === undefined
    ? undefined
    : resolveDutiesFromBackendDuties(duties);
}

/** The app-bar label — what the account IS, in the words the UI uses. */
export const ROLE_LABEL: Record<BolonRole, string> = {
  admin: "Admin",
  manager: "Manager",
  staff: "Staff",
};
